import { showNotificationBanner } from "../../store/actions";

/* Helper function to format items list with proper grammar */
const formatItemsList = (items) => {
  if (!items || items.length === 0) return "";

  if (items.length === 1) {
    return items[0];
  } else if (items.length === 2) {
    return `${items[0]} and ${items[1]}`;
  } else {
    const lastItem = items[items.length - 1];
    const otherItems = items.slice(0, -1).join(", ");
    return `${otherItems}, and ${lastItem}`;
  }
};

/* Helper function to show aggregated notifications (multiple items combined) */
export const showAggregatedNotification = (notificationType, priority, items, suffix = "", prefix = "", autoDismiss = true, isDismissible = true, failureItems = []) => {
  const hasSuccess = items && items.length > 0;
  const hasFailure = failureItems && failureItems.length > 0;

  // Case 1: All failed (no success items, only failures)
  if (!hasSuccess && hasFailure) {
    // Validate inputs for failure case
    if (!notificationType || !priority) {
      console.warn("showAggregatedNotification: notificationType and priority are required");
      return null;
    }

    const failureFormatted = formatItemsList(failureItems);
    const message = `${prefix} ${failureFormatted}`;

    return showNotificationBanner({
      type: notificationType,
      priority: "critical",
      data: { subTitle: message },
      showNotification: true,
      autoDismiss: autoDismiss,
      isDismissible: isDismissible,
      isAggregated: true
    });
  }

  // Case 2: Mixed (some success, some failed)
  if (hasSuccess && hasFailure) {
    // Validate inputs for mixed case
    if (!notificationType || !priority) {
      console.warn("showAggregatedNotification: notificationType and priority are required");
      return null;
    }

    const successFormatted = formatItemsList(items);
    const failureFormatted = formatItemsList(failureItems);
    const message = `${successFormatted} ${suffix}. However, ${prefix} ${failureFormatted}`;

    return showNotificationBanner({
      type: notificationType,
      priority: "warning",
      data: { subTitle: message },
      showNotification: true,
      autoDismiss: autoDismiss,
      isDismissible: isDismissible,
      isAggregated: true
    });
  }

  // Case 3: All success (validate inputs for success case)
  if (!notificationType || !priority) {
    console.warn("showAggregatedNotification: notificationType and priority are required");
    return null;
  }

  // Handle invalid or empty items array
  if (!Array.isArray(items) || items.length === 0) {
    console.warn("showAggregatedNotification: items must be a non-empty array");
    return null;
  }

  // Filter out falsy values and trim items
  const validItems = items
    .filter((item) => item && typeof item === "string")
    .map((item) => item.trim())
    .filter((item) => item.length > 0);

  // If no valid items after filtering, return early
  if (validItems.length === 0) {
    console.warn("showAggregatedNotification: no valid items provided");
    return null;
  }

  // Format items with proper grammar
  const combinedItems = formatItemsList(validItems);

  // Build final message with suffix only (prefix is for failures only)
  let formattedMessage = combinedItems;

  if (suffix) {
    formattedMessage = `${formattedMessage} ${suffix}`;
  }

  return showNotificationBanner({
    type: notificationType,
    priority: priority,
    data: {
      subTitle: formattedMessage
    },
    showNotification: true,
    autoDismiss: autoDismiss,
    isDismissible: isDismissible,
    isAggregated: true
  });
};
